import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';

import { StoreService } from '@services/stores.service';
import { SnackbarService } from '@services/snackbar.service';
import { AddressService } from '@services/address.service';

import { ADDRESSES_TAG, INT_NUMBER, NAME_REGEX } from '@shared/utils';
import { IAddressComponents, IAddressData } from '@models/address.model';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { GoogleMapsModule } from '@angular/google-maps';
import { AddressAutocompleteComponent } from '../../../shared/components/address-autocomplete/address-autocomplete.component';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
    selector: 'app-address-add',
    templateUrl: './address-add.component.html',
    styleUrls: ['./address-add.component.scss'],
    standalone: true,
    imports: [LottieComponent, MatFormFieldModule, NgClass, MatIconModule, MatDialogModule, AddressAutocompleteComponent, GoogleMapsModule, MatButtonModule, FormsModule, ReactiveFormsModule, MatInputModule, MatCheckboxModule, TranslateModule]
})
export class AddressAddComponent implements OnInit {

  public userLocation: any;
  public address: string;
  public tempAddress: any;
  public step = 0;
  public marker: any;
  public displayMap = false;
  public suburbs: any[] = [];
  public centerPosition: any;
  public addressTypeSelected: string;
  public tagButtonsList = ADDRESSES_TAG;
  public addressForm = new UntypedFormGroup({
    receiver: new UntypedFormControl('', [Validators.pattern(NAME_REGEX)]),
    street: new UntypedFormControl(''),
    number: new UntypedFormControl('', [/*Validators.required,*/ Validators.pattern(INT_NUMBER)]),
    intNum: new UntypedFormControl('', [Validators.pattern(INT_NUMBER)]),
    numberInt: new UntypedFormControl('', []),
    zipcode: new UntypedFormControl(''),
    suburb: new UntypedFormControl(''),
    town: new UntypedFormControl(''),
    state: new UntypedFormControl(''),
    references: new UntypedFormControl('', []),
    detail: new UntypedFormControl('', [Validators.required, this.noLeadingWhitespace]),
    default: new UntypedFormControl(false)
  });

  public options: AnimationOptions = {
    path: '/assets/animations/loader.json',
  };

  public loading = false;
  public loadingText = 'Cargando';
  public referenceAddress: string;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private addressService: AddressService,
    private snackbarService: SnackbarService,
    private dialogRef: MatDialogRef<AddressAddComponent>,
    @Inject(PLATFORM_ID) private platformId: string,
    private storeServices: StoreService
  ) { }

  ngOnInit(): void {
    this.data = {
      currentLocation: false,
      ...this.data
    };
    if (this.data?.detail) {
      const [number, intNum] = this.data.number?.split(',') ?? ['', ''];
      this.addressForm.patchValue({
        detail: this.data.detail,
        receiver: this.data.receiver,
        street: this.data.detail,
        number,
        intNum,
        references: this.data.detail2,
        zipcode: this.data.zipcode,
        suburb: this.data.suburb,
        town: this.data.city,
        state: this.data.state,
        default: this.data.default
      });
      this.addressTypeSelected = this.data.tag;
      this.getPlaceGeocode(1);
      this.goStep(1);
    }
  }

  private noLeadingWhitespace(control: AbstractControl): { [key: string]: any } | null {
    const isWhitespace = (control.value || '').startsWith(' ');
    return isWhitespace ? { 'leadingWhitespace': true } : null;
  }

  public selectAddressType(type: string): void {
    this.addressTypeSelected = type;
  }

  public async getPlaceGeocode(step: number): Promise<void> {
    if (this.data.edit === true || this.data.currentLocation === true) {
      this.marker = {
        position: { lat: this.data.coordinates[0], lng: this.data.coordinates[1] },
        options: {
          animation: google.maps.Animation.DROP,
          draggable: true
        }
      };
      this.centerPosition = { lat: this.data.coordinates[0], lng: this.data.coordinates[1] };

      if (this.data.currentLocation === true) {
        const geocoder = new google.maps.Geocoder();
        const location = new google.maps.LatLng(this.data.coordinates[0], this.data.coordinates[1]);
        geocoder.geocode({
          location
        }, (results: google.maps.GeocoderResult[] | null, status: string) => {
          if (status === 'OK') {
            if (results?.length) {
              const [res] = results;
              res.address_components.forEach((components: IAddressComponents) => {
                switch (components.types[0]) {
                  case 'street_number':
                    this.addressForm.patchValue({ number: components.short_name });
                    break;
                  case 'postal_code':
                    this.addressForm.patchValue({ zipcode: components.short_name });
                    break;
                  case 'administrative_area_level_1':
                    this.addressForm.patchValue({ state: components.long_name });
                    break;
                  case 'locality':
                    this.addressForm.patchValue({ town: components.long_name });
                    break;
                  case 'political':
                    if (components.types[1] === 'sublocality') {
                      this.addressForm.patchValue({ suburb: components.long_name });
                    }
                    break;
                  case 'route':
                    this.addressForm.patchValue({ street: components.short_name });
                    break;
                }
              });
              this.addressForm.patchValue({ detail: `${this.addressForm.get('street')?.value ? this.addressForm.get('street')?.value : ''}${this.addressForm.get('number')?.value ? ' # ' + this.addressForm.get('number')?.value : ''}` });
            }
          }
        });
      }

      this.goStep(step);
    } else {
      this.loading = true;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({
        address: `Calle ${this.addressForm.get('street')?.value}
        ${this.addressForm.get('number')?.value},
        ${this.addressForm.get('zipcode')?.value},
        ${this.addressForm.get('town')?.value},
        ${this.addressForm.get('state')?.value}, México.`
      }, async (results: google.maps.GeocoderResult[] | null, status) => {
        if (status === 'OK') {
          if (results?.length) {
            this.marker = {
              position: results[0].geometry.location,
              options: {
                animation: google.maps.Animation.DROP,
                draggable: true
              }
            };
            this.centerPosition = results[0].geometry.location;
            this.goStep(step);
            this.loading = false;
          }
        } else {
          this.loading = false;
          this.snackbarService.openSnackbar('ADDRESSES.addressNoFound', 'warn');
        }
      });
    }
  }

  async onCreateAddress(): Promise<void> {
    this.loading = true;
    const addressData: IAddressData = {
      coordinates: this.data ? [this.data.coordinates[0], this.data.coordinates[1]] :
        [this.marker.position.lat(), this.marker.position.lng()],
      detail: this.addressForm.get('detail')?.value,
      detail2: this.addressForm.get('references')?.value,
      tag: this.addressTypeSelected,
      save: true
    };
    if (this.addressForm.get('number')?.value) {
      addressData.number = this.addressForm.get('number')?.value;
    }
    if (this.addressForm.get('receiver')?.value) {
      addressData.receiver = this.addressForm.get('receiver')?.value;
    }
    if (this.addressForm.get('town')?.value) {
      addressData.city = this.addressForm.get('town')?.value;
    }
    if (this.addressForm.get('state')?.value) {
      addressData.state = this.addressForm.get('state')?.value;
    }
    if (this.addressForm.get('zipcode')?.value) {
      addressData.zipcode = this.addressForm.get('zipcode')?.value;
    }
    if (this.addressForm.get('suburb')?.value) {
      addressData.suburb = this.addressForm.get('suburb')?.value;
    }
    let response: any;
    try {
      if (this.data.edit === true) {
        response = await this.addressService.updateAddress(this.data.id.toString(), addressData);
        this.loading = false;
        this.dialogRef.close(response);
      } else {
        response = await this.addressService.addAddress(addressData);
        this.loading = false;
        this.dialogRef.close(response);
      }
    } catch (error) {
      this.loading = false;
    }
    if (this.addressForm.get('default')?.value === true) {
      try {
        await this.addressService.setAsDefault(response.data[0].id);
        const addressJSON = {
          type: 'delivery',
          displayName: `${response.data[0].detail}`,
          store: 999
        };
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('address', JSON.stringify(addressJSON));
        }
      } catch (error) { }
    }
  }

  public async moveMap(event: any): Promise<void> {
    this.loading = true;
    const data = {
      coordinates: {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }
    };
    this.centerPosition = data.coordinates;
    try {
      const res = await this.storeServices.getStoresByCoordsPolygon(data);
      if (res.data.FRESH_MARKET !== null || res.data.ALSUPER !== null || res.data.ALSUPER_PLUS !== null) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({
          location: event.latLng
        }, async (results: google.maps.GeocoderResult[] | null, status) => {
          if (status === 'OK') {
            if (results?.length) {
              const addressComp = results[0].address_components;
              addressComp.forEach((item: any) => {
                switch (item.types[0]) {
                  case 'street_number':
                    this.addressForm.patchValue({ number: item.short_name });
                    break;
                  case 'postal_code':
                    this.addressForm.patchValue({ zipcode: item.short_name });
                    break;
                  case 'administrative_area_level_1':
                    this.addressForm.patchValue({ state: item.long_name });
                    break;
                  case 'locality':
                    this.addressForm.patchValue({ town: item.long_name });
                    break;
                  case 'political':
                    if (item.types[1] === 'sublocality') {
                      this.addressForm.patchValue({ suburb: item.long_name });
                    }
                    break;
                  case 'route':
                    this.addressForm.patchValue({ street: item.short_name });
                    break;
                }
              });
              if (this.data.edit === true) {
                this.data.currentLocation = false;
                this.data.coordinates[0] = event.latLng.lat();
                this.data.coordinates[1] = event.latLng.lng();
              } else {
                this.marker.position = event.latLng;
              }
            }
            this.loading = false;
          }
        });
      } else {
        this.loading = false;
        this.snackbarService.openSnackbar('ADDRESSES.noExists', 'warn');
      }
    } catch (error) {
      this.loading = false;
      this.snackbarService.openSnackbar('ADDRESSES.noExists', 'warn');
    }
  }

  public async saveAddress(place: any): Promise<void> {

    if (place && place.geometry) {
      this.addressForm.reset();
      this.loading = true;

      this.loadingText = 'Buscando ubicación';
      this.data['coordinates'] = [
        place.geometry.location.lat(),
        place.geometry.location.lng()
      ];
      this.tempAddress = place;
      place.address_components.forEach((item: any) => {
        switch (item.types[0]) {
          case 'street_number':
            this.addressForm.patchValue({ number: item.long_name });
            break;
          case 'route':
            this.addressForm.patchValue({ street: item.long_name });
            break;
          case 'sublocality_level_1':
            this.addressForm.patchValue({ town: item.long_name });
            break;
          case 'locality':
            this.addressForm.patchValue({ state: item.long_name });
            break;
          case 'postal_code':
            this.addressForm.patchValue({ zipcode: item.long_name });
            break;
        }
      });
      if (!this.addressForm.get('street')?.value && !this.addressForm.get('number')?.value) {
        this.addressForm.patchValue({
          detail: null
        });
      } else {
        this.addressForm.patchValue({ detail: `${this.addressForm.get('street')?.value ? this.addressForm.get('street')?.value : ''}${this.addressForm.get('number')?.value ? ' # ' + this.addressForm.get('number')?.value : ''}` });
      }
      this.referenceAddress = `
      ${this.addressForm.get('street')?.value ? this.addressForm.get('street')?.value : ''}
      ${this.addressForm.get('number')?.value ? this.addressForm.get('number')?.value : ''}
      ${this.addressForm.get('suburb')?.value ? this.addressForm.get('suburb')?.value : ''}
      ${this.addressForm.get('zipcode')?.value ? this.addressForm.get('zipcode')?.value : ''}
      ${this.addressForm.get('town')?.value ? this.addressForm.get('town')?.value : ''}
      ${this.addressForm.get('state')?.value ? this.addressForm.get('state')?.value : ''}
    `;
      this.address = '';
      this.getPlaceGeocode(1);
    }
  }

  public goStep(index: number): void {
    this.step = index;
  }

  public goBackSelectAddress(): void {
    this.address = `${this.addressForm.get('street')?.value ? this.addressForm.get('street')?.value : ''} ${this.addressForm.get('number')?.value ? this.addressForm.get('number')?.value : ''} ${this.addressForm.get('suburb')?.value ? ', ' + this.addressForm.get('suburb')?.value : ''} ${this.addressForm.get('zipcode')?.value ? ', ' + this.addressForm.get('zipcode')?.value : ''} ${this.addressForm.get('town')?.value ? ', ' + this.addressForm.get('town')?.value : ''} ${this.addressForm.get('state')?.value ? ', ' + this.addressForm.get('state')?.value : ''}
    `;
    this.goStep(0);
  }

  public requestLocationPermissions(): void {
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      this.data.currentLocation = true;
      this.data.coordinates = [
        coords.latitude,
        coords.longitude
      ];
      this.address = '';
      this.getPlaceGeocode(1);
    });
  }

  public changeDirection(): void {
    this.goStep(0);
  }
}
